import type { GuidebookCollapseMode } from "../types/guidebook";

export const AUTO_COLLAPSE_LIMIT = 600;
export const COLLAPSED_PREVIEW_LENGTH = 180;

interface Endpoint {
  node: Node;
  offset: number;
}

const BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "LI",
  "BR",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "TR",
  "TD",
  "TH",
  "BLOCKQUOTE",
  "PRE",
  "SECTION",
  "ARTICLE",
  "UL",
  "OL",
]);

/** One normalized text index drives both the threshold and HTML range cuts. */
export const createGuidebookTextModel = (html: string) => {
  const document = new DOMParser().parseFromString(html, "text/html");
  document
    .querySelectorAll("script, style, template, noscript, [hidden]")
    .forEach((node) => node.remove());
  const characters: string[] = [];
  const endpoints: Endpoint[] = [];
  const blockEnds: Array<{ count: number; point: Endpoint }> = [];
  const append = (character: string, point: Endpoint) => {
    const normalized = /\s/.test(character) ? " " : character;
    if (
      normalized === " " &&
      (!characters.length || characters[characters.length - 1] === " ")
    )
      return;
    characters.push(normalized);
    endpoints.push(point);
  };
  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      let offset = 0;
      for (const character of node.textContent || "") {
        offset += character.length;
        append(character, { node, offset });
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as Element;
    // Embedded documents do not contribute to the guidebook's visible text.
    if (element.tagName === "IFRAME") return;
    const parent = node.parentNode!;
    const index = Array.prototype.indexOf.call(
      parent.childNodes,
      node,
    ) as number;
    const block = BLOCK_TAGS.has(element.tagName);
    if (block) append(" ", { node: parent, offset: index });
    Array.from(node.childNodes).forEach(visit);
    if (block) {
      const point = { node: parent, offset: index + 1 };
      const count =
        characters.length - (characters[characters.length - 1] === " " ? 1 : 0);
      if (count) blockEnds.push({ count, point });
      append(" ", point);
    }
  };
  Array.from(document.body.childNodes).forEach(visit);
  if (characters[characters.length - 1] === " ") {
    characters.pop();
    endpoints.pop();
  }
  const text = characters.join("");
  const getProgressiveHtml = (limit: number): string => {
    const count = Math.max(0, Math.floor(limit));
    if (count >= characters.length) return document.body.innerHTML;
    if (count === 0) return "";
    // Prefer nearby paragraph/list boundaries, but never repeat an early boundary
    // across subsequent 600-character steps (e.g. before a very long URL).
    const minimum = Math.max(1, count - COLLAPSED_PREVIEW_LENGTH);
    let point: Endpoint | undefined;
    for (const boundary of blockEnds) {
      if (boundary.count >= minimum && boundary.count <= count)
        point = boundary.point;
    }
    if (!point) {
      let end = count;
      while (end > minimum && characters[end - 1] !== " ") end -= 1;
      point = endpoints[(end > minimum ? end : count) - 1];
    }
    const range = document.createRange();
    range.setStart(document.body, 0);
    range.setEnd(point.node, point.offset);
    const container = document.createElement("div");
    container.append(range.cloneContents());
    return container.innerHTML;
  };
  return { text, characterCount: characters.length, getProgressiveHtml };
};

export const getGuidebookText = (html: string): string =>
  createGuidebookTextModel(html).text;
export const getProgressiveGuidebookContent = (
  html: string,
  limit: number,
): string => createGuidebookTextModel(html).getProgressiveHtml(limit);

export const shouldCollapseGuidebookText = (
  text: string,
  mode: GuidebookCollapseMode | null = "automatic",
): boolean =>
  text.length > 0 &&
  (mode === "collapsed" ||
    ((mode == null || mode === "automatic" || mode === "progressive") &&
      Array.from(text).length > AUTO_COLLAPSE_LIMIT));

export const getGuidebookTextPreview = (text: string): string => {
  const characters = Array.from(text);
  return characters.length > COLLAPSED_PREVIEW_LENGTH
    ? `${characters.slice(0, COLLAPSED_PREVIEW_LENGTH).join("").trimEnd()}…`
    : text;
};

/** Preserve plain-text entities and paragraph breaks before slicing as HTML. */
export const getGuidebookContentHtml = (
  content: string[],
  resolve: (html: string) => string,
  sanitize: (html: string) => string,
): string =>
  content
    .map((item) => {
      if (/<[^>]+>/.test(item) || /\{{\s*[^}]+\s*\}}/.test(item))
        return sanitize(resolve(item));
      const escaped = item
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
        .replace(/\r?\n/g, "<br>");
      return `<p>${escaped}</p>`;
    })
    .join("\n");
